import Chip from '@mui/material/Chip';
import {styled, useTheme} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import CustomTable from '../../components/CustomTable/CustomTable';
import FilterBar from '../../components/FilterBar/FilterBar';
import ToolBar from '../../components/ToolBar/ToolBar';
import {ConfigConstant} from '../../constants/config';
import RoutesConstant from '../../constants/routes';
import {getOpsConsolidatedData} from '../../service/OpsConsolidated';
import {pxToRem} from '../../utils';
import {
  formatChannelCode,
  getDateFromText,
  convertObjectToURLParams,
  getDefaultFilters,
  formatChannelName,
} from '../../utils/helper';

const StyledChip = styled(Chip)(({theme, label, type}) => ({
  width: pxToRem(100),
  fontWeight: 800,
  color:
    type === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
      ? theme.palette.success.dark
      : type === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
        ? theme.palette.error.dark
        : type === ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED
          ? theme.palette.warning.dark
          : theme.palette.grey[800],
  backgroundColor:
    type === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
      ? theme.palette.success.light3
      : type === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
        ? theme.palette.error.light3
        : type === ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED
          ? theme.palette.warning.light3
          : theme.palette.grey[200],
  '&:hover': {
    backgroundColor:
      type === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
        ? theme.palette.success.light2
        : type === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
          ? theme.palette.error.light2
          : type === ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED
            ? theme.palette.warning.light2
            : theme.palette.grey[300],
  },
}));

const OperationConsolidated = () => {
  const theme = useTheme();

  const legends = [];

  const commonFilters = useSelector((store) => store.commonFilters);

  const defaultFilters = getDefaultFilters(window?.location?.search, commonFilters);

  const [filters, setFilters] = useState([
    {
      id: 'Group By',
      displayValue: defaultFilters?.groupBy?.displayValue || 'Brand',
      value: defaultFilters?.groupBy?.value || 'partnerBrandCode-partnerBrandName',
      type: 'menu',
      selectType: 'single',
      key: 'groupBy',
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Date',
      displayValue: defaultFilters?.date?.displayValue || 'Today',
      value: defaultFilters?.date?.value || getDateFromText('Today'),
      type: 'date',
      selectType: 'single',
      key: 'date',
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Operation',
      type: 'menu',
      selectType: 'single',
      key: 'operationType',
      displayValue: defaultFilters?.operationType?.displayValue || ['Menu Push'],
      value: defaultFilters?.operationType?.value || ['MENU_PUSH'],
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Channel',
      type: 'menu',
      key: 'channelCode',
      selectType: 'multiple',
      displayValue: defaultFilters?.channelCode?.displayValue || [],
      value: defaultFilters?.channelCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.channelCode?.value) && defaultFilters?.channelCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Brand',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerBrandCode',
      displayValue: defaultFilters?.partnerBrandCode?.displayValue || [],
      value: defaultFilters?.partnerBrandCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerBrandCode?.value) && defaultFilters?.partnerBrandCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Store',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerPhyStoreCode',
      displayValue: defaultFilters?.partnerPhyStoreCode?.displayValue || [],
      value: defaultFilters?.partnerPhyStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerPhyStoreCode?.value) &&
        defaultFilters?.partnerPhyStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Virtual Store Code',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerStoreCode',
      displayValue: defaultFilters?.partnerStoreCode?.displayValue || [],
      value: defaultFilters?.partnerStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerStoreCode?.value) && defaultFilters?.partnerStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'City',
      type: 'menu',
      selectType: 'multiple',
      key: 'city',
      displayValue: defaultFilters?.city?.displayValue || [],
      value: defaultFilters?.city?.value || [],
      isApplied: Array.isArray(defaultFilters?.city?.value) && defaultFilters?.city?.value.length > 0 ? true : false,
      isDeleteable: true,
    },
  ]);

  const [{pageIndex, pageSize}, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const pageSizeOptions = [100, 200, 500];
  const dataQuery = useQuery({
    queryKey: ['data-opsConsolidated', {pageIndex, pageSize}],
    queryFn: () => getOpsConsolidatedData({pageIndex, pageSize, filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (dataQuery?.data?.totalOpsCount && dataQuery?.data?.totalOpsCount?.totalCount > 0) {
    legends.push(
      {
        label: 'Success:',
        data: `${(
          (dataQuery?.data?.totalOpsCount?.successCount / dataQuery?.data?.totalOpsCount?.totalCount) *
          100
        ).toFixed(2)}%`,
        textColor: theme.palette.success.light,
      },
      {
        label: 'Queued:',
        data: `${(
          (dataQuery?.data?.totalOpsCount?.queuedCount / dataQuery?.data?.totalOpsCount?.totalCount) *
          100
        ).toFixed(2)}%`,
        textColor: theme.palette.warning.light,
      },
      {
        label: 'Failed:',
        data: `${(
          (dataQuery?.data?.totalOpsCount?.failedCount / dataQuery?.data?.totalOpsCount?.totalCount) *
          100
        ).toFixed(2)}%`,
        textColor: theme.palette.error.light,
      }
    );
  }

  const handleRedirectToRaw = (row, status, accessor) => {
    const value = row?.[accessor];
    if (value > 0) {
      const defaultFilters = {};
      if (status) {
        defaultFilters.status = status;
      }
      let groupByFieldValue = '';
      filters?.forEach((filter) => {
        if (filter.id === 'Group By') {
          groupByFieldValue = filter.displayValue;
          const appliedGroupBy = filter?.value?.split(':');
          if (filter.displayValue === 'Channel') {
            defaultFilters['channelCode'] = [formatChannelCode(row)];
          } else {
            appliedGroupBy?.forEach((group) => {
              const [code] = group?.split('-') || [];
              defaultFilters[code] = [row?.[code]];
            });
          }
        } else if (filter?.isApplied && filter.id !== groupByFieldValue) {
          defaultFilters[filter?.key] = filter.value;
        }
      });
      const queryParams = convertObjectToURLParams(defaultFilters);
      window.open(`${RoutesConstant.OPS_RAW}?${queryParams}`, '_blank');
    }
  };

  const getInitialColumnVisibility = () => {
    if (defaultFilters?.groupBy?.displayValue) {
      return {
        Brand: defaultFilters.groupBy.displayValue === 'Brand',
        Channel: defaultFilters.groupBy.displayValue === 'Channel',
        Store: defaultFilters.groupBy.displayValue === 'Store',
        'Virtual Store Code': defaultFilters.groupBy.displayValue === 'Virtual Store Code',
        City: defaultFilters.groupBy.displayValue === 'City',
      };
    } else {
      return {
        Brand: true,
        Channel: false,
        Store: false,
        'Virtual Store Code': false,
        City: false,
      };
    }
  };
  const [columnVisibility, setColumnVisibility] = React.useState(getInitialColumnVisibility());

  const changeColumnVisibility = () => {
    const groupByFilter = filters.find((filter) => filter.id === 'Group By');
    if (groupByFilter && groupByFilter.displayValue) {
      setColumnVisibility((prevState) => {
        const newState = {
          Brand: false,
          Channel: false,
          Store: false,
          'Virtual Store Code': false,
          City: false,
        };
        newState[groupByFilter.displayValue] = true;
        return newState;
      });
    }
  };

  const columns = [
    {
      id: 'Brand',
      header: 'Brand',
      accessorKey: 'partnerBrandName',
      filterable: true,
    },
    {
      id: 'Store',
      header: 'Store',
      accessorKey: 'partnerPhyStoreName',
      filterable: true,
    },
    {
      id: 'Virtual Store Code',
      header: 'Virtual Store Code',
      accessorKey: 'partnerStoreCode',
      filterable: true,
    },
    {
      id: 'City',
      header: 'City',
      accessorKey: 'city',
      filterable: true,
    },
    {
      id: 'Channel',
      header: 'channel',
      accessorFn: (row) => formatChannelName(row),
      filterable: true,
    },
    {
      id: 'Success',
      header: 'Success',
      cell: ({row}) => {
        return (
          <StyledChip
            onClick={() =>
              handleRedirectToRaw(row?.original, ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS, 'successCount')
            }
            label={row.original?.successCount}
            type={ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS}
          />
        );
      },
    },
    {
      id: 'Queued',
      header: 'Queued',
      cell: ({row}) => {
        return (
          <StyledChip
            onClick={() =>
              handleRedirectToRaw(row?.original, ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED, 'queuedCount')
            }
            label={row.original?.queuedCount}
            type={ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED}
          />
        );
      },
    },
    {
      id: 'Failed',
      header: 'Failed',
      cell: ({row}) => {
        return (
          <StyledChip
            onClick={() =>
              handleRedirectToRaw(row?.original, ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED, 'failedCount')
            }
            label={row.original?.failedCount}
            type={ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED}
          />
        );
      },
    },
    {
      id: 'Total',
      header: 'Total',
      cell: ({row}) => {
        return (
          <StyledChip
            onClick={() => handleRedirectToRaw(row?.original, null, 'totalCount')}
            label={row.original?.totalCount}
            type={null}
          />
        );
      },
    },
  ];

  const getDataToExport = () => {
    if (Array.isArray(dataQuery.data?.rows) && dataQuery.data.rows.length > 0) {
      const dateFilter = filters.find((filter) => filter.id === 'Date');
      const fileName = `operationConsolidated(${format(new Date(dateFilter.value[0]), 'dd/MM/yyyy')}-${format(
        new Date(dateFilter.value[1]),
        'dd/MM/yyyy'
      )})`;
      const data = {
        fileName,
        dataToExport: `Operation Type, Brand Code, Brand, Successs, Queued, Failed, Total`,
      };
      dataQuery.data?.rows?.forEach(
        ({operationType, partnerBrandCode, partnerBrandName, successCount, queuedCount, failedCount, totalCount}) => {
          data.dataToExport += `\n${operationType},${partnerBrandCode},${partnerBrandName},${successCount},${queuedCount},${failedCount},${totalCount}`;
        }
      );
      return data;
    } else {
      return null;
    }
  };

  return (
    <>
      <ToolBar
        headerText={'Operations Consolidated'}
        showGlobalSearch={false}
        showLegends={true}
        legends={legends}
        showExportButton={{button: true, tooltip: false}}
        getDataToExport={getDataToExport}
      />
      <FilterBar
        dataQuery={[dataQuery]}
        filters={filters}
        setFilters={setFilters}
        showFilterActionButton={true}
        showApplyActionButton={true}
        allowDateRangeSelection={false}
        setPagination={setPagination}
        shouldValidateMultiOpsSelection={false}
        changeColumnVisibility={changeColumnVisibility}
      />
      <CustomTable
        tableKey={'ops-consolidated'}
        columns={columns}
        dataQuery={dataQuery}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pageIndex={pageIndex}
        setPagination={setPagination}
        filters={filters}
        setFilters={setFilters}
        allowDateRangeSelection={false}
        columnVisibility={columnVisibility}
      />
    </>
  );
};

export default OperationConsolidated;
